import { ScrollArea } from "@arcadeai/design-system/components/ui/atoms/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@arcadeai/design-system/components/ui/atoms/sheet";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useCreateUserSource, useUpdateUserSource } from "../api/hooks";
import {
  USER_SOURCES_ROUTE_ID,
  userSourcesSearchDefaults,
} from "../search-params";
import type { UserSource } from "../types";
import {
  DEFAULT_OIDC_USER_SOURCE_SCOPES,
  DEFAULT_OIDC_USER_SOURCE_SUBJECT_CLAIM,
  type UserSourceCreateRequest,
  type UserSourceUpdateRequest,
  userSourceCreateSchema,
  userSourceEditFormSchema,
} from "../types";
import { UserSourceForm } from "./form/user-source-form";

const CREATE_DEFAULTS: UserSourceCreateRequest = {
  name: "",
  description: "",
  issuer: "",
  client_id: "",
  client_secret: "",
  subject_claim: DEFAULT_OIDC_USER_SOURCE_SUBJECT_CLAIM,
  scopes: [...DEFAULT_OIDC_USER_SOURCE_SCOPES],
  status: "active",
};

type UserSourceSheetProps = {
  userSource?: UserSource;
  trigger: React.ReactNode;
};

export const UserSourceSheet = ({
  userSource,
  trigger,
}: UserSourceSheetProps) => {
  const { create } = useSearch({ from: USER_SOURCES_ROUTE_ID });
  const navigate = useNavigate();

  const isEditing = Boolean(userSource);

  const [isOpen, setIsOpen] = useState(create && !userSource);

  const defaultValues = useMemo((): UserSourceCreateRequest => {
    if (userSource) {
      return {
        name: userSource.name,
        description: userSource.description ?? "",
        issuer: userSource.issuer,
        client_id: userSource.client_id,
        client_secret: "",
        subject_claim: userSource.subject_claim,
        scopes:
          userSource.scopes && userSource.scopes.length > 0
            ? [...userSource.scopes]
            : [...DEFAULT_OIDC_USER_SOURCE_SCOPES],
        status: userSource.status,
      };
    }
    return CREATE_DEFAULTS;
  }, [userSource]);

  // Create mode requires client_secret; edit mode allows empty (= "keep existing").
  const form = useForm<UserSourceCreateRequest>({
    resolver: zodResolver(
      isEditing ? userSourceEditFormSchema : userSourceCreateSchema
    ),
    defaultValues,
  });

  const { mutate: createUserSource, isPending: isCreating } =
    useCreateUserSource();
  const { mutate: updateUserSource, isPending: isUpdating } =
    useUpdateUserSource(userSource?.id ?? "");
  const isPending = isCreating || isUpdating;

  const cleanUrl = useCallback(() => {
    if (create) {
      navigate({
        to: ".",
        search: userSourcesSearchDefaults,
        replace: true,
      });
    }
  }, [create, navigate]);

  const handleSubmit = useCallback(
    (values: UserSourceCreateRequest) => {
      // Error toasts are surfaced globally via meta.errorMessage in the hooks.
      if (isEditing) {
        // Status is mutated via the row toggle, not the sheet.
        // An empty client_secret means "keep existing" — drop it from the wire payload.
        const { status: _status, client_secret, ...rest } = values;
        const payload: UserSourceUpdateRequest =
          client_secret === "" ? rest : { ...rest, client_secret };
        updateUserSource(payload, {
          onSuccess: () => setIsOpen(false),
        });
      } else {
        createUserSource(values, {
          onSuccess: () => {
            setIsOpen(false);
            cleanUrl();
          },
        });
      }
    },
    [isEditing, createUserSource, updateUserSource, cleanUrl]
  );

  const handleError = useCallback(() => {
    toast.error("Some required information is missing or incorrect");
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        form.reset(defaultValues);
      }
      setIsOpen(open);
      if (!open) {
        cleanUrl();
      }
    },
    [defaultValues, form, cleanUrl]
  );

  return (
    <Sheet onOpenChange={handleOpenChange} open={isOpen}>
      {/* biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code */}
      {trigger && <SheetTrigger render={trigger as React.ReactElement} />}
      <SheetContent className="w-full px-2">
        <ScrollArea className="h-full w-full">
          <SheetHeader>
            <SheetTitle className="mt-2">
              {isEditing ? "Edit user source" : "Create user source"}
            </SheetTitle>
            <SheetDescription>
              {isEditing
                ? "Update this OIDC identity provider configuration."
                : "Configure an OpenID Connect identity provider for end-user authentication on your MCP gateways."}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 px-4 pb-4">
            <UserSourceForm
              form={form}
              isEditing={isEditing}
              isPending={isPending}
              onError={handleError}
              onSubmit={handleSubmit}
            />
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
